import { emptySplitApi } from '@/redux/base-api.ts';
import {
	GetInspirationsMetadataResponse,
	GetInspirationsRequest,
	GetInspirationsResponse,
	GetPresignedUrlResponse
} from './inspirations-api-types.ts';
import { Department, FlatInspirationItem, InspirationItemId } from './types.ts';
import { transformInspirationItemResponse } from './utils.ts';

const inspirationsApiSlice = emptySplitApi
	.enhanceEndpoints({ addTagTypes: ['Inspirations', 'InspirationsMetadata', 'InspirationItem'] })
	.injectEndpoints({
		endpoints: (build) => ({
			getInspirations: build.infiniteQuery<GetInspirationsResponse, GetInspirationsRequest, {}>({
				infiniteQueryOptions: {
					initialPageParam: {
						count: 20
					},
					getNextPageParam: (lastPage, _, lastPageParam) => {
						if (lastPage.pageInfo.hasNextPage) {
							return {
								...lastPageParam,
								pageToken: lastPage.pageInfo.nextPageToken
							};
						}
					}
				},
				query: ({ queryArg, pageParam }) => ({
					url: `/inspiration`,
					method: 'GET',
					params: { ...queryArg, ...pageParam }
				}),
				providesTags: ['Inspirations']
			}),

			getInspirationsMetadata: build.query<GetInspirationsMetadataResponse, Department>({
				query: (department) => ({
					url: `/inspiration/metadata`,
					method: 'GET',
					params: { department }
				}),
				providesTags: ['InspirationsMetadata']
			}),

			getInspirationItem: build.query<FlatInspirationItem, InspirationItemId>({
				query: (id) => ({
					url: `/inspiration/${id}`
				}),
				providesTags: (result, error, id) => [{ type: 'InspirationItem', id }],
				transformResponse: transformInspirationItemResponse
			}),

			createInspirationItem: build.mutation<void, FlatInspirationItem>({
				query: (body) => ({
					url: `/inspiration`,
					method: 'POST',
					body
				}),
				invalidatesTags: ['Inspirations']
			}),

			patchInspirationItem: build.mutation<void, FlatInspirationItem>({
				query: ({ id, ...body }) => ({
					url: `/inspiration/${id}`,
					method: 'PATCH',
					body
				}),
				invalidatesTags: (result, error, { id }) => [{ type: 'InspirationItem', id }, 'Inspirations']
			}),

			deleteInspirationItem: build.mutation<void, InspirationItemId>({
				query: (id) => ({
					url: `/inspiration/${id}`,
					method: 'DELETE'
				}),
				invalidatesTags: ['Inspirations']
			}),

			getPresignedUrl: build.mutation<GetPresignedUrlResponse, FormData>({
				query: (body) => ({
					url: `/inspiration/presigned-url`,
					method: 'POST',
					body
				})
			})
		})
	});

export const {
	useGetInspirationsInfiniteQuery,
	useGetInspirationsMetadataQuery,
	useGetInspirationItemQuery,
	useCreateInspirationItemMutation,
	usePatchInspirationItemMutation,
	useDeleteInspirationItemMutation,
	useGetPresignedUrlMutation
} = inspirationsApiSlice;
